import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { ChevronUp, ChevronDown, Lock, Info } from 'lucide-react';
import { NisabStandard } from '../../hooks/useZakatWizard';
interface RunningTotalProps {
  totals: {
    grossAssets: number;
    totalLiabilities: number;
    netAssets: number;
    goldNisabValue: number;
    silverNisabValue: number;
    currentNisabValue: number;
    zakatAmount: number;
    isZakatDue: boolean;
  };
  nisabStandard: NisabStandard;
  onNisabChange: (standard: NisabStandard) => void;
  emailSubmitted: boolean;
  constants: {
    GOLD_PRICE_USD_PER_GRAM: number;
    SILVER_PRICE_USD_PER_GRAM: number;
    GOLD_NISAB_GRAMS: number;
    SILVER_NISAB_GRAMS: number;
  };
}
export function RunningTotal({
  totals,
  nisabStandard,
  onNisabChange,
  emailSubmitted,
  constants
}: RunningTotalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showNisabInfo, setShowNisabInfo] = useState(false);
  // Consistent formatting: always 2 decimal places for currency
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val);
  const formatPrice = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val);
  return <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block sticky top-24 space-y-4" data-id="element-179">
        {/* Current Prices Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-gray-50 border-amber-100" data-id="element-180">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-181">
            Today's Prices
          </h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-182">
            <div data-id="element-183">
              <div className="text-xs text-gray-500" data-id="element-184">Gold</div>
              <div className="font-semibold text-amber-600" data-id="element-185">
                {formatPrice(constants.GOLD_PRICE_USD_PER_GRAM)}/g
              </div>
            </div>
            <div data-id="element-186">
              <div className="text-xs text-gray-500" data-id="element-187">Silver</div>
              <div className="font-semibold text-gray-500" data-id="element-188">
                {formatPrice(constants.SILVER_PRICE_USD_PER_GRAM)}/g
              </div>
            </div>
          </div>
        </Card>

        {/* Nisab Toggle Card */}
        <Card className="bg-white border-gray-200" data-id="element-189">
          <div className="flex items-center justify-between mb-2" data-id="element-190">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-191">
              Nisab (Threshold)
            </h4>
            <button onClick={() => setShowNisabInfo(!showNisabInfo)} className="text-gray-400 hover:text-gray-600" aria-label="What is Nisab?" data-id="element-192">
              <Info className="w-4 h-4" data-id="element-193" />
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-3" data-id="element-194">
            The Nisab is the minimum wealth you must own before Zakat becomes
            obligatory. It is based on the value of gold or silver.
          </p>

          <AnimatePresence data-id="element-195">
            {showNisabInfo && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="mb-4 text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-hidden" data-id="element-196">
                <p className="mb-2" data-id="element-197">
                  <strong data-id="element-198">Silver (Strict):</strong> Lower threshold (
                  {formatCurrency(totals.silverNisabValue)}). Most scholars
                  recommend this standard as it means more people pay Zakat,
                  benefiting more recipients.
                </p>
                <p data-id="element-199">
                  <strong data-id="element-200">Gold (Lenient):</strong> Higher threshold (
                  {formatCurrency(totals.goldNisabValue)}). Some scholars permit
                  this, especially for those with many dependents or financial
                  obligations.
                </p>
              </motion.div>}
          </AnimatePresence>

          <div className="grid grid-cols-2 gap-2" data-id="element-201">
            <button onClick={() => onNisabChange('silver')} className={`p-3 rounded-lg border-2 text-left transition-all ${nisabStandard === 'silver' ? 'border-[#2D9596] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-202">
              <div className="text-xs text-gray-500 mb-1" data-id="element-203">Silver (Strict)</div>
              <div className="font-bold text-gray-900 text-sm" data-id="element-204">
                {formatCurrency(totals.silverNisabValue)}
              </div>
              <div className="text-[10px] text-gray-400 mt-1" data-id="element-205">
                {constants.SILVER_NISAB_GRAMS}g
              </div>
            </button>
            <button onClick={() => onNisabChange('gold')} className={`p-3 rounded-lg border-2 text-left transition-all ${nisabStandard === 'gold' ? 'border-[#2D9596] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-206">
              <div className="text-xs text-gray-500 mb-1" data-id="element-207">Gold (Lenient)</div>
              <div className="font-bold text-gray-900 text-sm" data-id="element-208">
                {formatCurrency(totals.goldNisabValue)}
              </div>
              <div className="text-[10px] text-gray-400 mt-1" data-id="element-209">
                {constants.GOLD_NISAB_GRAMS}g
              </div>
            </button>
          </div>
        </Card>

        {/* Main Totals Card */}
        <Card className="bg-white border-teal-100 shadow-lg" data-id="element-210">
          <div className="text-center pb-4 border-b border-gray-100 mb-4" data-id="element-211">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-212">
              Estimated Zakat
            </h3>
            {emailSubmitted ? <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} className={`text-4xl font-bold font-serif ${totals.isZakatDue ? 'text-[#2D9596]' : 'text-gray-400'}`} data-id="element-213">
                {formatCurrency(totals.zakatAmount)}
              </motion.div> : <div className="py-2" data-id="element-214">
                <div className="inline-flex items-center gap-2 text-gray-400 bg-gray-50 px-4 py-2 rounded-full" data-id="element-215">
                  <Lock className="w-4 h-4" data-id="element-216" />
                  <span className="text-xs font-medium" data-id="element-217">
                    Complete all steps to reveal
                  </span>
                </div>
              </div>}
          </div>

          <div className="space-y-3 text-sm" data-id="element-218">
            <div className="flex justify-between text-gray-600" data-id="element-219">
              <span data-id="element-220">Total Assets</span>
              <span className="font-medium" data-id="element-221">
                {formatCurrency(totals.grossAssets)}
              </span>
            </div>
            <div className="flex justify-between text-red-500" data-id="element-222">
              <span data-id="element-223">Less Liabilities</span>
              <span className="font-medium" data-id="element-224">
                -{formatCurrency(totals.totalLiabilities)}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-100 font-bold text-gray-900" data-id="element-225">
              <span data-id="element-226">Net Wealth</span>
              <span data-id="element-227">{formatCurrency(totals.netAssets)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 pt-1" data-id="element-228">
              <span data-id="element-229">
                Nisab ({nisabStandard === 'silver' ? 'Silver' : 'Gold'})
              </span>
              <span data-id="element-230">{formatCurrency(totals.currentNisabValue)}</span>
            </div>
          </div>

          {!totals.isZakatDue && totals.netAssets > 0 && <div className="mt-4 p-2 bg-gray-50 rounded text-xs text-center text-gray-500" data-id="element-231">
              {formatCurrency(totals.currentNisabValue - totals.netAssets)} more
              needed to reach Nisab
            </div>}
        </Card>
      </div>

      {/* Mobile Bottom Sheet */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50" data-id="element-232">
        <div className="px-4 py-3 flex items-center justify-between cursor-pointer" onClick={() => setIsOpen(!isOpen)} data-id="element-233">
          <div data-id="element-234">
            <span className="text-xs text-gray-500 uppercase font-semibold" data-id="element-235">
              Zakat Due ({nisabStandard === 'silver' ? 'Silver' : 'Gold'})
            </span>
            {emailSubmitted ? <div className={`text-xl font-bold ${totals.isZakatDue ? 'text-[#2D9596]' : 'text-gray-900'}`} data-id="element-236">
                {formatCurrency(totals.zakatAmount)}
              </div> : <div className="flex items-center gap-2 text-gray-400" data-id="element-237">
                <Lock className="w-4 h-4" data-id="element-238" />
                <span className="text-sm" data-id="element-239">Complete all steps to see</span>
              </div>}
          </div>
          <button className="p-2 text-gray-400" data-id="element-240">
            {isOpen ? <ChevronDown data-id="element-241" /> : <ChevronUp data-id="element-242" />}
          </button>
        </div>

        <AnimatePresence data-id="element-243">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="px-4 pb-6 bg-gray-50 border-t border-gray-100 overflow-hidden" data-id="element-244">
              {/* Nisab Toggle */}
              <div className="grid grid-cols-2 gap-2 pt-4 mb-4" data-id="element-245">
                <button onClick={() => onNisabChange('silver')} className={`p-2 rounded-lg border text-xs ${nisabStandard === 'silver' ? 'border-[#2D9596] bg-teal-50 text-[#2D9596]' : 'border-gray-200 text-gray-600'}`} data-id="element-246">
                  Silver (Strict)
                </button>
                <button onClick={() => onNisabChange('gold')} className={`p-2 rounded-lg border text-xs ${nisabStandard === 'gold' ? 'border-[#2D9596] bg-teal-50 text-[#2D9596]' : 'border-gray-200 text-gray-600'}`} data-id="element-247">
                  Gold (Lenient)
                </button>
              </div>

              <div className="space-y-2 text-sm" data-id="element-248">
                <div className="flex justify-between text-gray-600" data-id="element-249">
                  <span data-id="element-250">Total Assets</span>
                  <span className="font-medium" data-id="element-251">
                    {formatCurrency(totals.grossAssets)}
                  </span>
                </div>
                <div className="flex justify-between text-red-500" data-id="element-252">
                  <span data-id="element-253">Less Liabilities</span>
                  <span className="font-medium" data-id="element-254">
                    -{formatCurrency(totals.totalLiabilities)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200 font-bold text-gray-900" data-id="element-255">
                  <span data-id="element-256">Net Wealth</span>
                  <span data-id="element-257">{formatCurrency(totals.netAssets)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500" data-id="element-258">
                  <span data-id="element-259">Nisab Threshold</span>
                  <span data-id="element-260">{formatCurrency(totals.currentNisabValue)}</span>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </>;
}